// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { RectangleEdgeFilter } from '@shared/commons/Graphics';
import { MovingType, ProMath, ToolView } from '../ToolView';
import { SelectionState, ToolSelectionType } from '../Selection';
import { TerceraChartTradingOperation } from '../../Utils/ChartConstants';
import { CancelCB } from './OrderToolView';
import { TradingToolViewBase, TradingToolViewType } from './TradingToolViewBase';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { SlTpPriceType } from '@shared/utils/Enums/Constants';
import { OffsetModeViewEnum } from '@shared/utils/Trading/OffsetModeViewEnum';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { PositionEdit } from '@shared/commons/cache/OrderParams/PositionEdit';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { OrderToolViewBase } from './OrderToolViewBase';
import { LayersEnum } from '../../Utils/LayersEnum';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
export class SLTPOrderToolView extends OrderToolViewBase {
    get isAllowOTHTrading() {
        if (!isNullOrUndefined(this.sltpOrder)) {
            return this.sltpOrder.IsAllowOTHTrading;
        }
        else {
            return this.parentOrder.IsAllowOTHTrading;
        }
    }
    constructor(order, sltpOrder, getToolViewByOrderId, terceraChartPanelContext, renderer, isSl) {
        super();
        this.parentY = -1;
        this.parentValue = NaN;
        this.errorMode = false;
        this.TradeWasModified = false;
        this.priceValue = NaN;
        this.priceY = -1;
        this.lastValidParentValue = NaN;
        this.lastValidPriceValue = NaN;
        this.sllTool = null; // for Stoplosslimit tool #91413
        this.movingType = MovingType.OnlyY;
        this.ToolViewType = TradingToolViewType.SLTPOrderToolView;
        this._isSl = !!isSl;
        this.collapsed = true;
        this.parentOrder = order;
        this.sltpOrder = sltpOrder;
        this.GetToolViewByOrderId = getToolViewByOrderId; // Func<string, TradingToolViewBase>
        this.terceraChartPanelContext = terceraChartPanelContext;
        this.myRenderer = renderer;
        if (this.parentOrder != null) {
            const isPosition = !!this.parentOrder.isPosition;
            const ID = isPosition ? this.parentOrder.PositionId : this.parentOrder.OrderNumber;
            const type = this.myRenderer.getToolViewTypeByOrder(this.parentOrder);
            const parentTool = this.GetToolViewByOrderId(ID, type);
            if (parentTool != null) {
                this.collapsed = parentTool.Collapsed;
            }
        }
        if (this.sltpOrder !== null) {
            this._isSl =
                this.parentOrder.SLOrder !== null &&
                    this.parentOrder.SLOrder.OrderNumber === this.sltpOrder.OrderNumber;
        }
        this.Updated();
    }
    // #region properties
    AllowCancelOrder() {
        return (IsAllowed.IsOrderCancelingAllowed(this.sltpOrder).Allowed ||
            (this.sltpOrder === null && IsAllowed.IsOrderModifyingAllowed(this.parentOrder).Allowed));
    }
    AllowTS() {
        return IsAllowed.IsTrailingStopForSLAllowed(this.parentOrder.Instrument, this.parentOrder.Account, this.parentOrder.isPosition);
    }
    AllowModify() {
        return ((IsAllowed.IsOrderModifyingAllowed(this.sltpOrder).Allowed ||
            (this.sltpOrder === null && IsAllowed.IsOrderModifyingAllowed(this.parentOrder).Allowed)) &&
            IsAllowed.IsOrderSLTPAllowed(this.parentOrder).Allowed);
    }
    get Collapsed() {
        return this.collapsed;
    }
    set Collapsed(value) {
        if (this.collapsed != value) {
            this.collapsed = value;
            if (this.parentOrder != null) {
                const isPosition = !!this.parentOrder.isPosition;
                const ID = isPosition ? this.parentOrder.PositionId : this.parentOrder.OrderNumber;
                const type = this.myRenderer.getToolViewTypeByOrder(this.parentOrder);
                this.GetToolViewByOrderId(ID, type).Collapsed = this.collapsed;
                const sllTool = this.sllTool;
                if (sllTool) {
                    sllTool.Collapsed = this.collapsed;
                }
            }
        }
    }
    get ID() {
        return this.sltpOrder !== null
            ? this.sltpOrder.OrderNumber
            : this.parentOrder.OrderNumber + (this.isSl ? '-SL' : '-TP');
    }
    get Price() {
        if (this.sltpOrder != null) {
            return this.sltpOrder.getPriceForStopLoss();
        }
        if (this.isSl) {
            return this.parentOrder.GetStopLossInPriceValue();
        }
        else {
            return this.parentOrder.GetTakeProfitInPriceValue();
        }
    }
    get isSl() {
        // if (this.sltpOrder != null)
        //    return this.sltpOrder.IsSLOrder;
        return this._isSl;
    }
    get Side() {
        if (this.sltpOrder != null) {
            return this.sltpOrder.BuySell;
        }
        return this.parentOrder.BuySell === OperationType.Buy ? OperationType.Sell : OperationType.Buy;
    }
    // #endregion
    Updated() {
        if (this.sltpOrder === null &&
            (this.parentOrder === null ||
                (isNaN(this.parentOrder.StopLossPriceValue) && isNaN(this.parentOrder.TakeProfitPriceValue)))) {
            return;
        }
        this.dataCacheTool.Points[0][0] = this.parentOrder.UTCDateTime;
        this.dataCacheTool.Points[0][1] = this.Price;
        if (this.sllTool) {
            this.sllTool.Updated();
        }
        if (!this.CurrentMovement.IsMovingRightNow) {
            this.CancelMoving();
        }
        this.myRenderer.chart.IsDirty(LayersEnum.Tools);
    }
    isTrailingStop() {
        if (this.sltpOrder != null) {
            return this.sltpOrder.OrderType === OrderType.TrailingStop;
        }
        return this._isSl && this.parentOrder.StopLossPriceType === SlTpPriceType.TrOffset;
    }
    CancelMoving() {
        this.TradeWasModified = false;
        this.ResetMove();
        this.ResetMouse();
    }
    Draw(gr, window, param) {
        this.MainOrderVisibility = false;
        if (!this.myRenderer.ShowOrders) {
            return;
        }
        const parentOrder = this.parentOrder;
        if (!(parentOrder === null || parentOrder === void 0 ? void 0 : parentOrder.Price)) {
            return;
        }
        if (this._isSl && this.sllTool && parentOrder.isPosition && parentOrder.IsActivated) {
            // #93540
            return;
        }
        if (IsAllowed.IsProtectiveOrders()) {
            return;
        } // #108469 <- 108200
        try {
            const CR = window.ClientRectangle;
            const minX = CR.X;
            const maxX = minX + CR.Width;
            let screenPoints00 = this.screenPoints[0][0];
            if (screenPoints00 < minX) {
                screenPoints00 = minX;
            }
            const offsetX = parentOrder.OrderType === OrderType.StopLimit
                ? this.getPlateOffset() * 2
                : minX + this.indentationLineLength + this.btnSize / 2;
            if (this.myRenderer.ShowVisualTradingOnLeftSide) {
                screenPoints00 = offsetX;
            }
            else {
                screenPoints00 += offsetX;
            }
            let screenPoints01 = this.screenPoints[0][1];
            if (!isNaN(this.priceValue)) {
                screenPoints01 = Math.floor(this.priceY);
            }
            // Ловим ошибки
            if ((screenPoints00 < 0 && maxX < 0) ||
                screenPoints01 >= ProMath.infinity ||
                screenPoints01 >= ProMath.infinity ||
                screenPoints01 <= ProMath.infinityMinus ||
                screenPoints01 <= ProMath.infinityMinus) {
                return;
            }
            let curX = screenPoints00;
            const curY = screenPoints01 - this.containerHeight / 2;
            this.imageRect = new Rectangle(curX, curY, this.btnSize, this.btnSize);
            curX += this.imageRect.Width;
            this.MainOrderVisibility = true;
            const hoveredOrSelected = this.CurrentSelection.CurrentState !== SelectionState.None;
            if (CR.Contains(screenPoints00, screenPoints01)) {
                const separators = [];
                const sltpText = this.isSl ? 'SL' : 'TP';
                separators.push(this.getVerticalSeparator(curX, curY));
                //#region IsAllowOTHTrading
                if (this.isAllowOTHTrading) {
                    this.overnightRect = new Rectangle(curX, curY, this.btnSize, this.btnSize);
                    curX += this.imageRect.Width;
                    separators.push(this.getVerticalSeparator(curX, curY));
                }
                else {
                    this.overnightRect = Rectangle.Empty();
                }
                //#endregion IsAllowOTHTrading
                const orderIsBuy = this.Side === OperationType.Buy;
                let shortDescriptionText = '';
                if (!this.Collapsed) {
                    shortDescriptionText = this.DescriptionText();
                    curX = this.addTextRectWithSeparator(gr, shortDescriptionText, curX, curY, separators, (r) => {
                        this.infoRect = r;
                    });
                    if (!this.errorMode) {
                        const isTrAllowed = this.AllowTS();
                        if (this._isSl && isTrAllowed.Allowed && !TradingLockUtils.TradingLock.tradingLocked) {
                            curX = this.addRectWithSeparator(curX, curY, this.btnSize, separators, (r) => {
                                this.trailingButton = r;
                            });
                        }
                        else {
                            this.trailingButton = Rectangle.Empty();
                        }
                        if (this.AllowCancelOrder() &&
                            !TradingLockUtils.TradingLock.tradingLocked &&
                            !param.TerceraChart.TerceraChartTradingToolsRenderer.NotTradingContext) {
                            curX = this.addRectWithSeparator(curX, curY, this.btnSize, separators, (r) => {
                                this.removeButton = r;
                            });
                        }
                        else {
                            this.removeButton = Rectangle.Empty();
                        }
                    }
                }
                else {
                    this.infoRect = Rectangle.Empty();
                    this.removeButton = Rectangle.Empty();
                    this.actionButton = Rectangle.Empty();
                    this.trailingButton = Rectangle.Empty();
                }
                this.mainContainer = new Rectangle(screenPoints00, curY, curX - screenPoints00, this.containerHeight);
                const { backgroundPen, backgroundBrush, TextBrush } = this.getDrawingTools(orderIsBuy, hoveredOrSelected);
                gr.RoundRectangle(this.mainContainer, 4, backgroundBrush);
                this.drawCenteredText(gr, sltpText, this.imageRect, TradingToolViewBase.tradeFont, TextBrush);
                this.drawIcon(gr, ThemeManager.NewImages.tableOrderOvernightIcon.image, this.overnightRect);
                this.drawCenteredText(gr, shortDescriptionText, this.infoRect, TradingToolViewBase.tradeFont, TextBrush);
                const trailingBrush = this.getButtonBrush(orderIsBuy, ToolSelectionType.Trailing === this.CurrentSelection.CurrentHover, this.isTrailingStop());
                this.drawImgButton(gr, this.trailingButton, ThemeManager.NewImages.trailingBtnDefault, ThemeManager.NewImages.trailingBtnHovered, trailingBrush);
                const removeBrush = this.getButtonBrush(orderIsBuy, ToolSelectionType.Remove === this.CurrentSelection.CurrentHover);
                this.drawImgButton(gr, this.removeButton, ThemeManager.NewImages.closeIconDefault, ThemeManager.NewImages.closeIconHovered, removeBrush, RectangleEdgeFilter.All & (RectangleEdgeFilter.BottomRight | RectangleEdgeFilter.TopRight));
                this.drawSeparators(gr, separators);
                const priceLinePen = this.getPriceLinePen(orderIsBuy);
                this.drawPriceLine(gr, this.mainContainer, maxX, screenPoints01, priceLinePen);
                const priceBrush = this.getPricePlateBrush(orderIsBuy);
                this.tryAddPlateOnPriceScale(param, this.priceValue, this.Price, priceBrush);
            }
            this.drawBindLines(gr, hoveredOrSelected, screenPoints01);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
    }
    GetCurrentSelection(x, y) {
        let newSelection = ToolSelectionType.None;
        if (this.imageRect.Contains(x, y)) {
            newSelection = ToolSelectionType.Image;
        }
        else if (this.actionButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Action;
        }
        else if (this.removeButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Remove;
        }
        else if (this.infoRect.Contains(x, y)) {
            newSelection = ToolSelectionType.Info;
        }
        else if (this.trailingButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Trailing;
        }
        else if (Math.abs(y - this.screenPoints[0][1]) < ToolView.POINT_DX) {
            newSelection = ToolSelectionType.Line;
        }
        else if (this.mainContainer.Contains(x, y)) {
            newSelection = ToolSelectionType.Container;
        }
        return newSelection;
    }
    UpdateScreenPoints(window, cashItemSeries = null) {
        super.UpdateScreenPoints(window, cashItemSeries);
        if (isNaN(this.parentValue) && isNaN(this.lastValidParentValue)) // для рисования линии связи
         {
            this.parentY = Math.floor(Math.round(window.PointsConverter.GetScreenY(this.parentOrder.Price)));
            if (!isNullOrUndefined(cashItemSeries)) {
                this.parentY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.parentOrder.Price))));
            }
        }
    }
    // #region mouse
    /// <summary>
    /// Является ли тулза выделенной мышью (сейчас по линиям, но можно и весь полигон)
    /// </summary>
    IsSelectCheck(x, y) {
        if (this.TradeWasModified) // тулза смещена и с сервера не пришло подтверждение не двигаем её
         {
            return false;
        }
        if (!this.myRenderer.ShowOrders) {
            return false;
        }
        const newSelection = this.GetCurrentSelection(x, y);
        return newSelection !== ToolSelectionType.None;
    }
    CheckMinDistanceForStartMoving(x, y) {
        const mouseDownHover = this.CurrentSelection.MouseDownHover;
        if (this.AllowModify() &&
            !TradingLockUtils.TradingLock.tradingLocked &&
            (mouseDownHover === ToolSelectionType.Image || mouseDownHover === ToolSelectionType.Info)) {
            return Math.abs(this.CurrentMovement.PrevY - y) >= this.MinDistanceForStartMoving();
        }
        return false;
    }
    // move
    ProcessNewPosition(window, x, y, cashItemSeries = null) {
        var _a;
        this.TradeWasModified = true;
        // move order
        if (this.AllowModify()) {
            super.ProcessNewPosition(window, x, y, cashItemSeries); // обновляем главного
            this.priceValue = window.PointsConverter.GetDataY(y);
            if (!isNullOrUndefined(cashItemSeries)) {
                this.priceValue = cashItemSeries.settings.RevertConverter(window.PointsConverter.GetDataY(y));
            }
            this.priceY = y;
            if (this.isSl) {
                const sllTool = this.sllTool;
                if (sllTool) {
                    sllTool.priceValue = sllTool.Price + (this.priceValue - this.Price);
                    sllTool.priceY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(sllTool.priceValue))));
                    sllTool.TradeWasModified = true;
                    sllTool.parentValue = this.priceValue;
                    sllTool.parentY = y;
                }
            }
            const ins = (_a = this.myRenderer) === null || _a === void 0 ? void 0 : _a.Instrument;
            const parentOrder = this.parentOrder;
            const isBuy = parentOrder.BuySell == OperationType.Buy;
            if (parentOrder.isPosition) {
                const prVal = ins.roundPrice(this.priceValue);
                const closePrice = PositionEdit.SlTpPriceGetter(this._isSl, isBuy, parentOrder, ins, parentOrder.Account, parentOrder.LastQuote);
                if (this._isSl) {
                    this.errorMode = isBuy ? prVal > closePrice : prVal < closePrice;
                }
                else {
                    this.errorMode = isBuy ? prVal < closePrice : prVal > closePrice;
                }
            }
            else {
                const parentPrice = parentOrder.Price;
                const prVal = ins.roundPrice(this.priceValue);
                if (this._isSl) {
                    this.errorMode = isBuy ? prVal >= parentPrice : prVal <= parentPrice;
                }
                else {
                    this.errorMode = isBuy ? prVal <= parentPrice : prVal >= parentPrice;
                }
            }
        }
    }
    // mouse finish
    ProcessMoveFinish() {
        var _a;
        try {
            super.ProcessMoveFinish();
            if (this.errorMode) {
                this.undoChanges();
                return;
            }
            const mouseDownHover = this.CurrentSelection.MouseDownHover;
            if (mouseDownHover === ToolSelectionType.Image ||
                mouseDownHover === ToolSelectionType.Info ||
                mouseDownHover === ToolSelectionType.Line) {
                const ins = (_a = this.myRenderer) === null || _a === void 0 ? void 0 : _a.Instrument;
                const newPrice = ins ? ins.roundPrice(this.priceValue) : this.priceValue;
                let newSLLPrice = null;
                const trailing = this.isTrailingStop();
                let delta = newPrice;
                this.lastValidPriceValue = newPrice;
                if (trailing) {
                    // const parentPrice = this.parentOrder.isPosition ? this.parentOrder.CurPriceClose : this.parentOrder.Price;
                    // if (trailing)
                    //    delta = (this.parentOrder.BuySell == OperationType.Buy ? (parentPrice - newPrice) : (newPrice - parentPrice)) / this.myRenderer.Instrument.priceIncrement;;
                    delta = ins ? ins.roundPrice(delta) : delta;
                }
                if (this.isSl) {
                    const cancel = new CancelCB();
                    const sllTool = this.sllTool;
                    if (sllTool) {
                        newSLLPrice = ins ? ins.roundPrice(sllTool.priceValue) : sllTool.priceValue;
                        sllTool.lastValidParentValue = newPrice;
                        cancel.add(sllTool.Updated.bind(sllTool));
                    }
                }
                this.terceraChartPanelContext.ChartVisualTrading(this.parentOrder, {
                    action: this._isSl ? TerceraChartTradingOperation.ModifySL : TerceraChartTradingOperation.ModifyTP,
                    price: delta,
                    sllPrice: newSLLPrice,
                    ts: trailing,
                    CancelCallback: this.undoChanges.bind(this)
                });
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
            this.ResetMouse();
            super.ProcessMoveFinish();
        }
    }
    // mouse click
    ProcessClick(e, isMobile = false) {
        if (this.CurrentSelection.MouseDownHover === this.CurrentSelection.CurrentHover) {
            switch (this.CurrentSelection.MouseDownHover) {
                case ToolSelectionType.Image:
                case ToolSelectionType.Info:
                    if (!isMobile) {
                        this.Collapsed = !this.Collapsed;
                    }
                    break;
                case ToolSelectionType.Action:
                    if (!TradingLockUtils.TradingLock.tradingLocked) // this.terceraChartPanelContext.ChartVisualTrading(this.sltpOrder, TerceraChartTradingOperation.this.actionButton);
                     {
                        this.terceraChartPanelContext.ChartVisualTrading(this.sltpOrder, {
                            action: TerceraChartTradingOperation.ActionButton
                        });
                    }
                    // show menu
                    break;
                case ToolSelectionType.Remove:
                    if (!TradingLockUtils.TradingLock.tradingLocked) // this.terceraChartPanelContext.ChartVisualTrading(this.parentOrder, this._isSl ? TerceraChartTradingOperation.RemoveSL : TerceraChartTradingOperation.RemoveTP, null);
                     {
                        this.terceraChartPanelContext.ChartVisualTrading(this.parentOrder, {
                            action: this._isSl
                                ? TerceraChartTradingOperation.RemoveSL
                                : TerceraChartTradingOperation.RemoveTP
                        });
                    }
                    break;
                case ToolSelectionType.Trailing:
                    if (!TradingLockUtils.TradingLock.tradingLocked) {
                        const trailing = !this.isTrailingStop();
                        // const parentPrice = this.parentOrder.isPosition ? this.parentOrder.CurPriceClose : this.parentOrder.Price;
                        let delta = this.Price;
                        // if (trailing)
                        //    delta = (this.parentOrder.BuySell == OperationType.Buy ? (parentPrice - this.Price) : (this.Price - parentPrice)) / this.myRenderer.Instrument.priceIncrement;
                        delta =
                            this.myRenderer.Instrument != null ? this.myRenderer.Instrument.roundPrice(delta) : delta;
                        // this.terceraChartPanelContext.ChartVisualTrading(this.parentOrder, TerceraChartTradingOperation.ModifySL, new TerceraChartTradingOperationValues() { ts = trailing, price = delta });
                        this.terceraChartPanelContext.ChartVisualTrading(this.parentOrder, {
                            action: TerceraChartTradingOperation.ModifySL,
                            ts: trailing,
                            price: delta
                        });
                    }
                    break;
                case ToolSelectionType.Line:
                case ToolSelectionType.None:
                default:
                    break;
            }
        }
        this.ResetMouse();
    }
    ResetMouse() {
        this.CurrentSelection.MouseDownHover = ToolSelectionType.None;
        this.errorMode = false;
        this.priceValue = NaN;
        if (isNaN(this.lastValidPriceValue)) {
            this.priceY = NaN;
        }
    }
    ResetMove() {
        this.lastValidPriceValue = NaN;
        this.lastValidParentValue = NaN;
        this.priceY = NaN;
        this.TradeWasModified = false;
        const tool = this.sllTool;
        if (tool) {
            tool.lastValidPriceValue = tool.parentValue = NaN;
        }
    }
    // TODO. Rename. Refactor.
    undoChanges() {
        this.ResetMove();
        this.Updated();
        const tool = this.sllTool;
        if (tool) {
            tool.undoChanges();
        }
    }
    GetCursor(e) {
        const curElement = this.CurrentSelection.MouseDownHover === ToolSelectionType.None
            ? this.CurrentSelection.CurrentHover
            : this.CurrentSelection.MouseDownHover;
        switch (curElement) {
            case ToolSelectionType.Image:
            case ToolSelectionType.Info:
                return this.AllowModify() ? (this.errorMode ? Cursors.No : Cursors.NoMoveVert) : null;
            case ToolSelectionType.Action:
            case ToolSelectionType.Remove:
            case ToolSelectionType.Line:
            case ToolSelectionType.None:
            case ToolSelectionType.Trailing:
            default:
                return null;
        }
    }
    GetTooltip(e) {
        let res = '';
        switch (this.CurrentSelection.CurrentHover) {
            case ToolSelectionType.Image:
                res = Resources.getResource('TradeTool.Order');
                break;
            case ToolSelectionType.Info:
                res = Resources.getResource('TradeTool.Tick and P/L');
                break;
            case ToolSelectionType.Remove:
                res = Resources.getResource('TradeTool.Cancel order');
                break;
            default:
                res = null;
                break;
        }
        return res ? super.GetTooltip(e) : res;
    }
    // #endregion
    Dispose() {
        this.GetToolViewByOrderId = null;
        this.parentOrder = null;
        this.sltpOrder = null;
        super.Dispose();
    }
    DescriptionText() {
        var _a;
        const isSlPresent = this.parentOrder.isPosition
            ? ((_a = this.parentOrder.SLOrder) === null || _a === void 0 ? void 0 : _a.StopLimit) && this.parentOrder.SLOrder.OrderType != OrderType.TrailingStop
            : this.parentOrder.StopLossLimitPriceValue != null;
        if (this.isSl && isSlPresent && this.sllTool) {
            return '';
        }
        // от цены открытия.
        const isMovedParentOrder = !isNaN(this.parentValue);
        let orderPrice = this.parentOrder.Price;
        if (isMovedParentOrder) {
            orderPrice = this.parentValue;
        }
        else if (!isNaN(this.lastValidParentValue)) // #49010 - здесь восстанавливаем значение цены, мышку мы отпустили и parentValue == NaN, но ордер еще не выставился
         {
            orderPrice = this.lastValidParentValue;
        }
        let curPrice = this.priceValue;
        if (!isNaN(this.priceValue)) {
            this.lastValidPriceValue = this.priceValue;
        }
        if (isNaN(this.priceValue) && !isNaN(this.lastValidPriceValue)) {
            curPrice = this.lastValidPriceValue;
        }
        else {
            curPrice = this.priceValue;
        }
        const ins = this.myRenderer.instrument;
        /// revertedPrice
        const revertedPrice = curPrice; // 87781 - округление //!= Nn ? ins.roundPrice(curPrice) : curPrice;
        // orderPrice = ins.roundPrice(orderPrice);
        const sltpPrice = !isNaN(revertedPrice) ? revertedPrice : this.Price;
        const absoluteOffset = sltpPrice - orderPrice;
        const ticksVisualOffset = SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(ins, BaseSettings.offsetType);
        const offsetTicks = SlTpUtils.calculateTicks(orderPrice, sltpPrice, ins, BaseSettings.offsetType);
        const offsetTicksStr = `${SlTpUtils.formatSLTPValue(SlTpUtils.toVisualValue(offsetTicks, ins, ticksVisualOffset), ins, ticksVisualOffset)}${SlTpUtils.getSymbolForOffsetMode(ticksVisualOffset)}`;
        let shortDescriptionText;
        if (!this.errorMode) {
            const pnl = this.parentOrder.Account.formatPrice(TradingToolViewBase.FuturesSlTp(this.parentOrder, absoluteOffset));
            if (!this.isTrailingStop()) {
                let offsetPercent;
                const sltpHolder = this.parentOrder.createSlTpHolder();
                const isHasCloseOrder = this.isSl ? !sltpHolder.isSlEmpty() : !sltpHolder.isTpEmpty();
                const isCloseOrderInPrecent = this.isSl
                    ? sltpHolder.StopLossPriceType === SlTpPriceType.Percent
                    : sltpHolder.TakeProfitPriceType === SlTpPriceType.Percent;
                if (isMovedParentOrder && isHasCloseOrder && isCloseOrderInPrecent) {
                    offsetPercent = this.isSl ? sltpHolder.StopLossPriceValue : sltpHolder.TakeProfitPriceValue;
                }
                else {
                    offsetPercent = SlTpUtils.calculatePercent(orderPrice, sltpPrice);
                }
                const offsetPercentStr = `${SlTpUtils.formatSLTPValue(SlTpUtils.toVisualValue(offsetPercent, ins, OffsetModeViewEnum.Percent), ins, OffsetModeViewEnum.Percent)}${SlTpUtils.getSymbolForOffsetMode(OffsetModeViewEnum.Percent)}`;
                shortDescriptionText = `${pnl} ${offsetTicksStr} ${offsetPercentStr}`;
            }
            else {
                shortDescriptionText = `${pnl} ${offsetTicksStr}`;
            }
        }
        else {
            shortDescriptionText = Resources.getResource('chart.visualTrading.Invalid price');
        }
        return shortDescriptionText;
    }
    drawBindLines(gr, hoveredOrSelected, screenPoints01) {
        if (this.Collapsed && !hoveredOrSelected && !this.TradeWasModified) {
            return;
        }
        let pen = null;
        if (this.parentOrder.BuySell === OperationType.Sell) {
            pen = this.TradeWasModified ? TradingToolViewBase.chartOrderModifiedPen : this.myRenderer.orderSellPen;
        }
        else {
            pen = this.TradeWasModified ? TradingToolViewBase.chartOrderModifiedPen : this.myRenderer.orderBuyPen;
        }
        const lineLenght = this.indentationLineLength;
        if (this.parentY > screenPoints01) {
            gr.DrawLine(pen, this.imageRect.X - lineLenght, this.imageRect.Y + this.imageRect.Height / 2, this.imageRect.X - lineLenght, this.parentY - this.imageRect.Height / 2);
            gr.DrawLine(pen, this.imageRect.X - lineLenght, this.imageRect.Y + this.imageRect.Height / 2, this.imageRect.X, this.imageRect.Y + this.imageRect.Height / 2);
        }
        else {
            gr.DrawLine(pen, this.imageRect.X - lineLenght, this.imageRect.Y + this.imageRect.Height / 2, this.imageRect.X - lineLenght, this.parentY + this.imageRect.Height / 2);
            gr.DrawLine(pen, this.imageRect.X - lineLenght, this.imageRect.Y + this.imageRect.Height / 2, this.imageRect.X, this.imageRect.Y + this.imageRect.Height / 2);
        }
    }
    getPriceLinePen(isBuy) {
        if (!isBuy) {
            return this.TradeWasModified ? TradingToolViewBase.chartOrderModifiedPen : this.myRenderer.orderSellPen;
        }
        else {
            return this.TradeWasModified ? TradingToolViewBase.chartOrderModifiedPen : this.myRenderer.orderBuyPen;
        }
    }
}
